import { NavLink } from "react-router-dom";
import { FaBars, FaHome } from "react-icons/fa";
import { MdMessage, MdDeviceHub, MdBusiness } from "react-icons/md";
import { FiUsers, FiMap } from "react-icons/fi";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useRole } from "../../context/RoleContext";
import { showOrgNav, showSitesNav, showUsersNav } from "../../utils/permissions";

// Build nav groups filtered by role permissions
function buildNavGroups(role) {
  const groups = [];

  // Main — always visible to all roles
  groups.push({
    label: "Main",
    routes: [
      { path: "/",             name: "Dashboard",    icon: <FaHome size={16} /> },
      { path: "/devices",      name: "Devices",      icon: <MdDeviceHub size={17} /> },
      { path: "/notification", name: "Notification", icon: <MdMessage size={17} /> },
    ],
  });

  // Manage — Sites (Super Admin, Org Admin, Site Admin) and Users (same + Site Admin)
  const manageRoutes = [];
  if (showSitesNav(role)) {
    manageRoutes.push({ path: "/sites", name: "Sites", icon: <FiMap size={16} /> });
  }
  if (showUsersNav(role)) {
    manageRoutes.push({ path: "/users", name: "Users", icon: <FiUsers size={16} /> });
  }
  if (manageRoutes.length > 0) {
    groups.push({ label: "Manage", routes: manageRoutes });
  }

  // Admin — Organizations: Super Admin only
  if (showOrgNav(role)) {
    groups.push({
      label: "Admin",
      routes: [
        { path: "/organizations", name: "Organizations", icon: <MdBusiness size={17} /> },
      ],
    });
  }

  return groups;
}

const labelAnim = {
  hidden: { width: 0, opacity: 0, transition: { duration: 0.25 } },
  show:   { opacity: 1, width: "auto", transition: { duration: 0.25, delay: 0.1 } },
};

const SideBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen(o => !o);
  const { currentRole } = useRole();

  const navGroups = buildNavGroups(currentRole);

  return (
    <motion.div
      animate={{
        width: isOpen ? "210px" : "58px",
        transition: { duration: 0.35, type: "spring", damping: 18, stiffness: 200 },
      }}
      className="sidebar"
    >
      {/* Hamburger */}
      <div className="top_section">
        <div className="bars" onClick={toggle}>
          <FaBars />
        </div>
      </div>

      {/* Nav groups */}
      <section className="routes">
        {navGroups.map((group, gi) => (
          <div key={gi}>
            {/* Section separator (not first group) */}
            {gi > 0 && <div className="sidebar-divider" />}

            {/* Section label — only visible when expanded */}
            <AnimatePresence>
              {isOpen && (
                <motion.div
                  variants={labelAnim}
                  initial="hidden"
                  animate="show"
                  exit="hidden"
                  className="sidebar-section-label"
                >
                  {group.label}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Routes */}
            {group.routes.map((route, ri) => (
              <NavLink
                key={ri}
                to={route.path}
                end={route.path === "/"}
                className="link"
              >
                <div className="icon">{route.icon}</div>
                <AnimatePresence>
                  {isOpen && (
                    <motion.span
                      variants={labelAnim}
                      initial="hidden"
                      animate="show"
                      exit="hidden"
                      className="link_text"
                    >
                      {route.name}
                    </motion.span>
                  )}
                </AnimatePresence>
              </NavLink>
            ))}
          </div>
        ))}
      </section>
    </motion.div>
  );
};

export default SideBar;
